import React, { useState, useCallback } from 'react';

// ── Types ──────────────────────────────────────────────────────────
interface QuizOption {
  id: string;
  label: string;
  /** Which cognitive region this answer feeds */
  region: string;
  /** Node type injected on selection */
  nodeType: 'preference' | 'behavior' | 'fact' | 'boundary';
  /** Node value text to add */
  nodeValue: string;
}

interface QuizQuestion {
  id: string;
  category: string;
  categoryIcon: string;
  question: string;
  description: string;
  options: QuizOption[];
}

interface Props {
  replicaId: string;
  onNodeSuggestion?: (region: string, type: string, value: string) => void;
}

// ── Question Bank ──────────────────────────────────────────────────
const QUIZ_QUESTIONS: QuizQuestion[] = [
  // ─── Identity & Personality ───
  {
    id: 'q-decision',
    category: 'Decision Making',
    categoryIcon: '🧭',
    question: 'When facing a difficult decision, what do you rely on most?',
    description: 'This helps your replica understand your reasoning style.',
    options: [
      { id: 'a1', label: 'Data and logical analysis', region: 'identity', nodeType: 'behavior', nodeValue: 'Relies primarily on data-driven, analytical reasoning for decisions' },
      { id: 'a2', label: 'Gut feeling and intuition', region: 'identity', nodeType: 'behavior', nodeValue: 'Relies on intuition and gut feeling for important decisions' },
      { id: 'a3', label: 'Advice from trusted people', region: 'communication', nodeType: 'behavior', nodeValue: 'Prefers consulting trusted advisors before making decisions' },
      { id: 'a4', label: 'A structured pros/cons framework', region: 'identity', nodeType: 'behavior', nodeValue: 'Uses structured frameworks (pros/cons lists) for decision-making' },
    ],
  },
  {
    id: 'q-conflict',
    category: 'Conflict Resolution',
    categoryIcon: '⚖️',
    question: 'How do you typically handle disagreements?',
    description: 'Defines how your replica navigates interpersonal tension.',
    options: [
      { id: 'b1', label: 'Address it head-on, directly', region: 'communication', nodeType: 'behavior', nodeValue: 'Addresses disagreements directly and assertively' },
      { id: 'b2', label: 'Find a compromise that works for everyone', region: 'communication', nodeType: 'behavior', nodeValue: 'Seeks compromise and win-win solutions in conflicts' },
      { id: 'b3', label: 'Take time to reflect before responding', region: 'emotion', nodeType: 'behavior', nodeValue: 'Takes time to reflect and cool down before addressing conflicts' },
      { id: 'b4', label: 'Avoid conflict, preserve harmony', region: 'communication', nodeType: 'behavior', nodeValue: 'Prefers to avoid direct confrontation, values harmony' },
    ],
  },
  {
    id: 'q-communication',
    category: 'Communication Style',
    categoryIcon: '💬',
    question: 'What best describes how you communicate?',
    description: 'Shapes how your replica writes emails, messages, and responses.',
    options: [
      { id: 'c1', label: 'Short, concise, to the point', region: 'communication', nodeType: 'preference', nodeValue: 'Prefers brief, direct communication without unnecessary detail' },
      { id: 'c2', label: 'Warm, empathetic, personal', region: 'communication', nodeType: 'preference', nodeValue: 'Uses warm, empathetic tone with personal touches in communication' },
      { id: 'c3', label: 'Formal, structured, professional', region: 'communication', nodeType: 'preference', nodeValue: 'Maintains formal, structured, and professional communication style' },
      { id: 'c4', label: 'Casual, friendly, uses humor', region: 'communication', nodeType: 'preference', nodeValue: 'Communicates casually with humor and a friendly tone' },
    ],
  },
  {
    id: 'q-priority',
    category: 'Work Priorities',
    categoryIcon: '🎯',
    question: 'When managing tasks, what matters most to you?',
    description: 'Teaches your replica how to prioritize on your behalf.',
    options: [
      { id: 'd1', label: 'Meeting deadlines above all else', region: 'skills', nodeType: 'preference', nodeValue: 'Prioritizes meeting deadlines over perfection' },
      { id: 'd2', label: 'Quality and thoroughness', region: 'skills', nodeType: 'preference', nodeValue: 'Prioritizes quality and thoroughness over speed' },
      { id: 'd3', label: 'Team alignment and buy-in', region: 'skills', nodeType: 'preference', nodeValue: 'Prioritizes team consensus and alignment before executing' },
      { id: 'd4', label: 'Innovation and creative solutions', region: 'skills', nodeType: 'preference', nodeValue: 'Prioritizes creative and innovative approaches over conventional methods' },
    ],
  },
  {
    id: 'q-boundaries',
    category: 'Ethical Boundaries',
    categoryIcon: '🛡️',
    question: 'What should your replica NEVER do on your behalf?',
    description: 'Sets hard boundaries in the ethics core of your replica\'s brain.',
    options: [
      { id: 'e1', label: 'Make financial commitments', region: 'ethics', nodeType: 'boundary', nodeValue: 'Must never make financial commitments or spending decisions autonomously' },
      { id: 'e2', label: 'Share personal/sensitive information', region: 'ethics', nodeType: 'boundary', nodeValue: 'Must never share personal or sensitive information without explicit approval' },
      { id: 'e3', label: 'Respond emotionally to criticism', region: 'ethics', nodeType: 'boundary', nodeValue: 'Must never respond with emotion or defensiveness to criticism' },
      { id: 'e4', label: 'Act without confirming first', region: 'governance', nodeType: 'boundary', nodeValue: 'Must always confirm with owner before taking any significant action' },
    ],
  },
  {
    id: 'q-memory',
    category: 'Memory Style',
    categoryIcon: '💾',
    question: 'What kind of details do you naturally remember?',
    description: 'Helps your replica know what memories to prioritize storing.',
    options: [
      { id: 'f1', label: 'Names, dates, and numbers', region: 'memory', nodeType: 'preference', nodeValue: 'Strong recall for factual data: names, dates, figures' },
      { id: 'f2', label: 'Emotional context and the "vibe" of events', region: 'memory', nodeType: 'preference', nodeValue: 'Remembers events by emotional context and atmosphere over details' },
      { id: 'f3', label: 'Lessons learned and patterns', region: 'memory', nodeType: 'preference', nodeValue: 'Retains patterns, lessons, and strategic insights from experiences' },
      { id: 'f4', label: 'People\'s preferences and habits', region: 'memory', nodeType: 'preference', nodeValue: 'Naturally remembers people\'s preferences, habits, and personal details' },
    ],
  },
  {
    id: 'q-stress',
    category: 'Stress Response',
    categoryIcon: '🌊',
    question: 'How do you react under pressure?',
    description: 'Teaches your replica how to behave during high-stakes moments.',
    options: [
      { id: 'g1', label: 'Stay calm and focused, compartmentalize', region: 'emotion', nodeType: 'behavior', nodeValue: 'Remains calm and compartmentalizes under pressure' },
      { id: 'g2', label: 'Become more energized and decisive', region: 'emotion', nodeType: 'behavior', nodeValue: 'Thrives under pressure; becomes more decisive and energized' },
      { id: 'g3', label: 'Need time alone to process', region: 'emotion', nodeType: 'behavior', nodeValue: 'Needs solitude and quiet time to process stressful situations' },
      { id: 'g4', label: 'Seek support from others', region: 'emotion', nodeType: 'behavior', nodeValue: 'Reaches out to trusted people for emotional support under stress' },
    ],
  },
  {
    id: 'q-learning',
    category: 'Learning Style',
    categoryIcon: '📚',
    question: 'How do you learn new things best?',
    description: 'Affects how your replica absorbs and organizes new knowledge.',
    options: [
      { id: 'h1', label: 'Reading and researching deeply', region: 'skills', nodeType: 'preference', nodeValue: 'Learns best through deep reading and independent research' },
      { id: 'h2', label: 'Hands-on experimentation', region: 'skills', nodeType: 'preference', nodeValue: 'Learns best by doing — hands-on experimentation and trial' },
      { id: 'h3', label: 'Watching others and imitating', region: 'skills', nodeType: 'preference', nodeValue: 'Learns best by observing experts and mimicking techniques' },
      { id: 'h4', label: 'Discussing and debating with others', region: 'skills', nodeType: 'preference', nodeValue: 'Learns best through discussion, debate, and collaborative exploration' },
    ],
  },
];

// ── Main Component ─────────────────────────────────────────────────
const CognitiveQuiz: React.FC<Props> = ({ replicaId, onNodeSuggestion }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, QuizOption>>({});
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);

  const question = QUIZ_QUESTIONS[currentIndex];
  const progress = ((Object.keys(answers).length) / QUIZ_QUESTIONS.length) * 100;

  const handleSelect = useCallback((option: QuizOption) => {
    setSelectedOption(option.id);
  }, []);

  const handleNext = useCallback(async () => {
    if (!selectedOption || !question) return;
    const chosen = question.options.find(o => o.id === selectedOption);
    if (!chosen) return;

    setIsSubmitting(true);

    // Record answer
    const newAnswers = { ...answers, [question.id]: chosen };
    setAnswers(newAnswers);

    // Save node to backend FIRST, then notify parent to refresh graph
    try {
      const token = localStorage.getItem('accessToken');
      const API = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      await fetch(`${API}/api/v1/cognitive/${replicaId}/nodes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          type: chosen.nodeType,
          value: chosen.nodeValue,
          confidence: 0.8,
          source: 'quiz',
          region: chosen.region,
        }),
      });
    } catch {
      // non-blocking — node will be suggested even if save fails
    }

    // Fire callback AFTER save so graph reloads fresh data
    if (onNodeSuggestion) {
      onNodeSuggestion(chosen.region, chosen.nodeType, chosen.nodeValue);
    }

    setIsSubmitting(false);
    setSelectedOption(null);

    if (currentIndex < QUIZ_QUESTIONS.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setCompleted(true);
    }
  }, [selectedOption, question, answers, currentIndex, replicaId, onNodeSuggestion]);

  const handleRestart = () => {
    setCurrentIndex(0);
    setAnswers({});
    setSelectedOption(null);
    setCompleted(false);
  };

  // ── Completion Screen ───
  if (completed) {
    const regionCounts: Record<string, number> = {};
    Object.values(answers).forEach(a => {
      regionCounts[a.region] = (regionCounts[a.region] || 0) + 1;
    });

    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-lg text-center">
          {/* Celebration */}
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-indigo-500/20 to-violet-500/20 border border-indigo-500/30 flex items-center justify-center">
            <span className="text-4xl">🧠</span>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Cognitive Profile Enhanced</h2>
          <p className="text-sm text-slate-400 mb-6">
            {Object.keys(answers).length} new cognitive nodes were added to your replica's brain.
          </p>

          {/* Region impact summary */}
          <div className="grid grid-cols-2 gap-2 mb-6">
            {Object.entries(regionCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([region, count]) => (
                <div key={region} className="flex items-center gap-2 px-3 py-2 bg-slate-800/50 border border-slate-700/50 rounded-xl">
                  <span className={`w-2 h-2 rounded-full ${
                    region === 'identity' ? 'bg-indigo-500' :
                    region === 'communication' ? 'bg-cyan-500' :
                    region === 'emotion' ? 'bg-rose-500' :
                    region === 'skills' ? 'bg-emerald-500' :
                    region === 'memory' ? 'bg-amber-500' :
                    region === 'ethics' ? 'bg-red-500' :
                    'bg-purple-500'
                  }`} />
                  <span className="text-xs text-slate-300 capitalize flex-1 text-left">{region}</span>
                  <span className="text-xs text-slate-500 font-mono">+{count}</span>
                </div>
              ))}
          </div>

          <div className="flex gap-3 justify-center">
            <button
              onClick={handleRestart}
              className="px-5 py-2.5 text-sm text-slate-400 border border-slate-600/50 rounded-xl hover:text-white hover:border-slate-500 transition-colors"
            >
              Retake Quiz
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Quiz UI ───
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Progress bar */}
      <div className="px-6 pt-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-slate-500">
            Question {currentIndex + 1} of {QUIZ_QUESTIONS.length}
          </span>
          <span className="text-xs text-slate-500 font-mono">{Math.round(progress)}%</span>
        </div>
        <div className="w-full h-1 bg-slate-700/60 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-cyan-400 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question card */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-xl w-full">
          {/* Category badge */}
          <div className="flex items-center gap-2 mb-4">
            <span className="text-lg">{question.categoryIcon}</span>
            <span className="text-xs font-semibold text-indigo-400 uppercase tracking-wider">
              {question.category}
            </span>
          </div>

          {/* Question */}
          <h2 className="text-lg font-bold text-white mb-1.5 leading-snug">
            {question.question}
          </h2>
          <p className="text-xs text-slate-400 mb-6">{question.description}</p>

          {/* Answers */}
          <div className="space-y-2.5">
            {question.options.map((opt) => {
              const isSelected = selectedOption === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => handleSelect(opt)}
                  className={`w-full text-left flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-all duration-200 ${
                    isSelected
                      ? 'bg-indigo-900/30 border-indigo-500/50 shadow-lg shadow-indigo-900/20'
                      : 'bg-slate-800/30 border-slate-700/50 hover:bg-slate-700/30 hover:border-slate-600/50'
                  }`}
                >
                  {/* Radio dot */}
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    isSelected ? 'border-indigo-500' : 'border-slate-600'
                  }`}>
                    {isSelected && (
                      <div className="w-2 h-2 rounded-full bg-indigo-500" />
                    )}
                  </div>
                  <span className={`text-sm ${isSelected ? 'text-white font-medium' : 'text-slate-300'}`}>
                    {opt.label}
                  </span>
                  {/* Region tag */}
                  <span className="ml-auto text-[9px] text-slate-500 px-1.5 py-0.5 bg-slate-800/50 rounded capitalize">
                    → {opt.region}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Submit */}
          <div className="flex items-center justify-between mt-6">
            <button
              onClick={() => {
                if (currentIndex > 0) {
                  setCurrentIndex(prev => prev - 1);
                  setSelectedOption(answers[QUIZ_QUESTIONS[currentIndex - 1]?.id]?.id || null);
                }
              }}
              disabled={currentIndex === 0}
              className="text-xs text-slate-500 hover:text-white disabled:opacity-30 transition-colors"
            >
              ← Previous
            </button>
            <button
              onClick={handleNext}
              disabled={!selectedOption || isSubmitting}
              className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white text-sm font-semibold rounded-xl transition-all"
            >
              {isSubmitting ? 'Saving...' : currentIndex === QUIZ_QUESTIONS.length - 1 ? 'Complete' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CognitiveQuiz;
